import { useEffect, useMemo, useState } from "react"

const DEFAULT_ROWS_PER_PAGE = 10

type ListPaginationControls = Readonly<{
    totalItems: number
    rowsPerPage: number
    currentPage: number
    setRowsPerPage: (rows: number) => void
    setCurrentPage: (page: number) => void
}>

type ListPaginationState<T> = Readonly<{
    pagedItems: ReadonlyArray<T>
    pagination: ListPaginationControls
}>

export function useListPagination<T>(items: ReadonlyArray<T>): ListPaginationState<T> {
    const [rowsPerPage, setRowsPerPageState] = useState<number>(DEFAULT_ROWS_PER_PAGE)
    const [currentPage, setCurrentPage] = useState(1)

    const totalItems = items.length
    const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage))

    // Keep the page in range when the underlying list shrinks (e.g. filtering by period).
    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages)
        }
    }, [currentPage, totalPages])

    const pagedItems = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage
        return items.slice(start, start + rowsPerPage)
    }, [items, currentPage, rowsPerPage])

    const setRowsPerPage = (rows: number) => {
        setRowsPerPageState(rows)
        setCurrentPage(1)
    }

    return {
        pagedItems,
        pagination: { totalItems, rowsPerPage, currentPage, setRowsPerPage, setCurrentPage },
    }
}
